// Scroll management for the WebView client. BrowserRouter (not a data router) has no
// built-in <ScrollRestoration>, and the app had none — so detail pages could open
// mid-scroll and Back lost the previous position. This component:
//   • PUSH/REPLACE → scroll the new page to the top (fixes "opens not from the top")
//   • POP (back)   → restore the saved position for that history entry
// Positions are kept per react-router location.key (survives the single-document SPA).
// Restore retries for a few frames because client pages mount their content async
// (token + data), so the page may be short at first paint. Bounded + self-cancelling.
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const positions = new Map();
const MAX_RESTORE_FRAMES = 40; // ~0.6s cap

export default function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();
  const keyRef = useRef(location.key);

  // Continuously remember the current page's scroll so a later Back can restore it.
  useEffect(() => {
    keyRef.current = location.key;
    const save = () => positions.set(location.key, window.scrollY);
    window.addEventListener('scroll', save, { passive: true });
    return () => {
      positions.set(location.key, window.scrollY); // final save as we leave this entry
      window.removeEventListener('scroll', save);
    };
  }, [location.key]);

  useLayoutEffect(() => {
    if (navType === 'POP') {
      const target = positions.get(location.key) ?? 0;
      if (target <= 0) { window.scrollTo(0, 0); return undefined; }
      let raf;
      let tries = 0;
      const restore = () => {
        window.scrollTo(0, target);
        tries += 1;
        if (window.scrollY < target - 2 && tries < MAX_RESTORE_FRAMES) {
          raf = requestAnimationFrame(restore);
        }
      };
      restore();
      return () => raf && cancelAnimationFrame(raf);
    }
    // PUSH / REPLACE → new screen starts at the top
    window.scrollTo(0, 0);
    return undefined;
  }, [location.key, navType]);

  return null;
}
